import React, { useState } from 'react';
import { 
  Modal, 
  ModalDialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  Stack, 
  Alert,
  Divider
} from '@mui/joy';
import { KeyRounded } from '@mui/icons-material';
import MailAPI, { ChangePasswordRequest } from '../../api.ts';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const api = new MailAPI();

export default function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<ChangePasswordRequest>({
    oldPassword: '',
    newPassword: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.changePassword(formData);
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ oldPassword: '', newPassword: '' });
    setError('');
    setSuccess(false);
    onClose();
  };

  const handleChange = (field: keyof ChangePasswordRequest) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog 
        variant="outlined" 
        role="dialog" 
        aria-labelledby="change-password-title"
        sx={{ 
          width: 400, 
          minHeight: 350,
          maxHeight: 500,
          overflow: 'auto'
        }}
      >
        <DialogTitle id="change-password-title">
          <KeyRounded />
          Change Password
        </DialogTitle>
        <Divider />
        
        <DialogContent>
          {error && (
            <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert color="success" variant="soft" sx={{ mb: 2 }}>
              Password changed successfully!
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Current Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Enter your current password"
                  value={formData.oldPassword}
                  onChange={handleChange('oldPassword')}
                  disabled={loading || success}
                />
              </FormControl>

              <FormControl required>
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={handleChange('newPassword')}
                  disabled={loading || success}
                />
              </FormControl>
            </Stack>
          </form>
        </DialogContent>

        <DialogActions>
          <Button
            variant="solid"
            color="primary"
            loading={loading}
            disabled={success}
            onClick={handleSubmit}
          >
            Change Password
          </Button>
          <Button variant="plain" color="neutral" onClick={handleClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
